import { Repeat, Timer } from 'lucide-react'
import { formatCurrency } from '@/lib/format'
import type { TravelAddOns } from '@/types/api'
import { Badge } from '@/components/ui/StatusBadge'
import styles from './AddOnBadges.module.css'

/**
 * The return leg and extra hours on an inter-city booking, as chips.
 *
 * Shared by the Trip Requests table, the approval dialog, the Trips table and
 * the trip drawer, so a round trip is spelled the same way on every screen.
 * Renders nothing when nothing was added — which is every city booking and
 * every booking made before add-ons existed — so it can sit unconditionally
 * next to a package name.
 *
 * Deliberately distinct in wording from the trip's *extension* hours, which
 * are bought mid-journey: "Return leg" and "+6 h in Cairo" are what the
 * customer chose when they booked, and the fare already includes them. The
 * area matters to dispatch — hours in Cairo and hours at the coast are
 * different days for the driver.
 */
export function AddOnBadges({ addOns, priced = false }: { addOns: TravelAddOns; priced?: boolean }) {
  if (!addOns.any) return null
  const area = addOns.extraHoursArea?.trim() || null

  return (
    <span className={styles.badges} aria-label={`Add-ons: ${addOns.summary ?? ''}`}>
      {addOns.returnLeg ? (
        <Badge tone="brand" title="The driver brings the passenger back — there and back on one booking">
          <Repeat aria-hidden="true" className={styles.icon} />
          Return leg
          {priced ? <span className={styles.price}>{formatCurrency(addOns.returnLegAmount)}</span> : null}
        </Badge>
      ) : null}
      {addOns.extraHours > 0 ? (
        <Badge
          tone="info"
          title={`${addOns.extraHours} extra hours with the driver${area ? ` in ${area}` : ''}`}
        >
          <Timer aria-hidden="true" className={styles.icon} />+{addOns.extraHours} h
          {area ? <span className={styles.area}>in {area}</span> : null}
          {priced ? <span className={styles.price}>{formatCurrency(addOns.extraHoursAmount)}</span> : null}
        </Badge>
      ) : null}
    </span>
  )
}
